import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { changePassword } from '@/api/auth/auth.api'
import { AppDialog } from '@/components/AppDialog'
import { PasswordInput } from '@/components/PasswordInput'
import { Button } from '@/components/ui/button'

type PasswordFormData = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const defaultValues: PasswordFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

interface ChangePasswordDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export default function ChangePasswordDialog({ isOpen, onOpenChange }: ChangePasswordDialogProps) {
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<PasswordFormData>({
    defaultValues,
  })

  const newPassword = watch('newPassword')

  const onSubmit = async (data: PasswordFormData) => {
    setError('')

    if (data.newPassword !== data.confirmPassword) {
      setError('הסיסמאות שהכנסת לא זהות')
      return
    }

    if (data.newPassword.length < 8) {
      setError('על הסיסמה לכלול לפחות 8 תווים')
      return
    }

    if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/.test(data.newPassword)) {
      setError('סיסמה לא תקינה')
      return
    }

    try {
      setIsLoading(true)
      await changePassword(data.currentPassword, data.newPassword)
      onOpenChange(false)
      reset(defaultValues)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { msg?: string } } }
      setError(error?.response?.data?.msg || 'שגיאה בעדכון הסיסמה')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset(defaultValues)
    setError('')
    onOpenChange(false)
  }

  return (
    <AppDialog isOpen={isOpen} onOpenChange={handleClose} title='שינוי סיסמה' dialogContentClassName='pt-4'>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col items-end gap-8'>
        <div className='flex w-full flex-col items-end gap-[21px]'>
          <Controller
            name='currentPassword'
            control={control}
            rules={{ required: 'סיסמה נוכחית הוא שדה חובה' }}
            render={({ field }) => (
              <PasswordInput
                id='current-password'
                label='סיסמה נוכחית'
                {...field}
                error={!!errors.currentPassword}
                supportingText={errors.currentPassword?.message}
                required
              />
            )}
          />
          <Controller
            name='newPassword'
            control={control}
            rules={{
              required: 'סיסמה חדשה הוא שדה חובה',
              minLength: { value: 8, message: 'על הסיסמה לכלול לפחות 8 תווים' },
            }}
            render={({ field }) => (
              <PasswordInput
                id='new-password'
                label='סיסמה חדשה'
                {...field}
                error={!!errors.newPassword}
                supportingText={errors.newPassword?.message}
                required
              />
            )}
          />
          <Controller
            name='confirmPassword'
            control={control}
            rules={{
              required: 'אישור סיסמה הוא שדה חובה',
              validate: value => value === newPassword || 'הסיסמאות שהכנסת לא זהות',
            }}
            render={({ field }) => (
              <PasswordInput
                id='confirm-password'
                label='אשר סיסמה חדשה'
                {...field}
                error={!!errors.confirmPassword}
                supportingText={errors.confirmPassword?.message}
                required
              />
            )}
          />
          {error && <div className='text-destructive w-full text-end text-sm'>{error}</div>}
        </div>
        <div className='flex w-full flex-col items-center gap-2'>
          <Button
            type='submit'
            disabled={isLoading}
            className='h-12 w-full max-w-[335px] rounded-full bg-[#FAD600] font-medium text-[#333333] hover:bg-[#FAD600]/90'
          >
            {isLoading ? 'שומר…' : 'שמירת סיסמה חדשה'}
          </Button>
        </div>
      </form>
    </AppDialog>
  )
}
